import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  styled,
  Card,
  CardContent,
  CardActions,
  Grid,
  useMediaQuery,
  useTheme,
  IconButton
} from '@mui/material';
import {
  Kitchen as KitchenIcon,
  RestaurantMenu as RestaurantMenuIcon,
  ArrowBack as ArrowBackIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { storage } from '../../firebase';
import firebaseDataService from '../../services/FirebaseDataService';
import { mainApi } from '../../config/apiClient';
import { getScreenPermissionsByType } from '../../services/migration/personnelDataService';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
// Header artık MainLayout tarafından sağlanıyor, kendi Header'ımızı kullanmıyoruz
import QRMenuSettingsModal from '../qr-menu/modal/QRMenuSettingsModal';

// Styled Components - MainLayout içinde render edildiği için height: 100vh kaldırıldı
const PageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100%',
  backgroundColor: '#f5f5f5',
}));

const ContentArea = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(3),
  overflow: 'auto',
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

// Mobil menü kartı
const MenuCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(2),
  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
  border: '1px solid',
  borderColor: theme.palette.divider,
  transition: 'all 0.2s ease',
  '&:hover': {
    boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
    transform: 'translateY(-2px)',
  },
}));

// Interfaces
interface QRMenu {
  id: string;
  name: string;
  branchName?: string;
}

interface KitchenPermissions {
  accessKitchenScreen: boolean;
  viewAllOrders: boolean;
  updateOrderStatus: boolean;
  cancelOrders: boolean;
}

const KitchenMenuSelection: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { t } = useTranslation('salesSystem');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // State
  const [menus, setMenus] = useState<QRMenu[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [selectedMenuForSettings, setSelectedMenuForSettings] = useState<QRMenu | null>(null);

  // Şirket ID'sini ve kullanıcı rolünü al
  useEffect(() => {
    const fetchUserData = async () => {
      if (currentUser) {
        try {
          const userData = await firebaseDataService.getUserData(currentUser.uid);
          if (userData) {
            setCompanyId(userData.companyId || '');
            setUserRole(userData.role || '');
          }
        } catch (error) {
          console.error('❌ Kullanıcı bilgileri alınamadı:', error);
          setError(t('kitchenMenuSelection.errors.userInfoLoadFailed'));
        }
      }
    };
    fetchUserData();
  }, [currentUser, t]);

  // QR Menüleri yükle
  useEffect(() => {
    const fetchMenus = async () => {
      if (!companyId) return;

      setLoading(true);
      setError(null);

      try {
        const response = await mainApi.get(`/api/QRMenu/menu-definitions`);
        const result = response.data;
        if (result.success && result.data) {
          const menuList: QRMenu[] = result.data.map((menu: any) => ({
            id: menu.id,
            name: menu.name || t('kitchenMenuSelection.table.unnamedMenu'),
            branchName: menu.branchName
          }));
          
          if (userRole === 'admin') {
            setMenus(menuList);
          } else {
            const authorizedMenus = await filterAuthorizedMenus(menuList);
            setMenus(authorizedMenus);
          }
        }
      } catch (error) {
        console.error('❌ Menü yükleme hatası:', error);
        setError(t('kitchenMenuSelection.errors.menuLoadFailed'));
      } finally {
        setLoading(false);
      }
    };

    if (companyId && userRole) {
      fetchMenus();
    }
  }, [companyId, userRole, currentUser, t]);

  // Yetkili menüleri filtrele (personel için)
  const filterAuthorizedMenus = async (allMenus: QRMenu[]): Promise<QRMenu[]> => {
    if (!currentUser || !companyId) return [];

    try {
      const authorizedMenus: QRMenu[] = [];

      // kitchenScreens yetkileri SADECE SQL'den tek seferde (screenId->settings haritasi).
      const allowedByScreenId = new Map<string, KitchenPermissions>();
      try {
        const rows = await getScreenPermissionsByType(currentUser.uid, 'kitchenScreens');
        rows.forEach((r) => {
          if (r.isEnabled && r.screenId != null && r.settings) {
            allowedByScreenId.set(String(r.screenId), r.settings as KitchenPermissions);
          }
        });
      } catch (e) {
        console.warn('[KitchenMenuSelection] SQL yetki okuma hatasi:', e);
      }

      for (const menu of allMenus) {
        const permissions: KitchenPermissions | undefined = allowedByScreenId.get(String(menu.id));
        if (permissions && permissions.accessKitchenScreen) {
          authorizedMenus.push(menu);
        }
      }
      
      return authorizedMenus;
    } catch (error) {
      console.error('❌ Yetki kontrolü hatası:', error);
      return [];
    }
  };

  // Mutfak ekranına git
  const handleSelectMenu = (menuId: string) => {
    navigate(`/mutfak-ekrani/${menuId}`);
  };

  // Geri git
  const handleGoBack = () => {
    // Browser history'den bir önceki sayfaya git
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      // History yoksa varsayılan sayfaya git
      navigate('/qr-menu');
    }
  };

  // Ayarlar modalını aç
  const handleOpenSettingsModal = (menu: QRMenu) => {
    setSelectedMenuForSettings(menu);
    setIsSettingsModalOpen(true);
  };

  // Ayarlar modalını kapat
  const handleCloseSettingsModal = () => {
    setSelectedMenuForSettings(null);
    setIsSettingsModalOpen(false);
  };

  // Ürün resmi yükleme (QRMenuSettingsModal için gerekli)
  const handleUploadProductImage = async (productId: string, file: File): Promise<string> => {
    if (!companyId) {
      throw new Error(t('kitchenMenuSelection.errors.companyInfoMissing'));
    }
    
    try {
      const productImageRef = storageRef(storage, `companies/${companyId}/products/${productId}/image`);
      await uploadBytes(productImageRef, file);
      const downloadUrl = await getDownloadURL(productImageRef);
      return downloadUrl;
    } catch (error) {
      console.error('Resim yükleme hatası:', error);
      throw new Error(t('kitchenMenuSelection.errors.imageUploadFailed'));
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <ContentArea>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
            <CircularProgress size={60} />
          </Box>
        </ContentArea>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ContentArea sx={{ p: isMobile ? 2 : 3 }}>
        {/* Başlık Alanı - Mobil Uyumlu */}
        <Box sx={{ mb: isMobile ? 3 : 4 }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'flex-start' : 'center', 
            gap: isMobile ? 1.5 : 0,
            mb: 2 
          }}>
            {/* Mobilde: Geri butonu ve başlık yan yana */}
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <IconButton
                onClick={handleGoBack}
                sx={{ 
                  mr: 1,
                  bgcolor: 'grey.100',
                  '&:hover': { bgcolor: 'grey.200' }
                }}
              >
                <ArrowBackIcon />
              </IconButton>
              <KitchenIcon sx={{ fontSize: isMobile ? 28 : 32, mr: 1, color: 'primary.main' }} />
              <Typography variant={isMobile ? "h5" : "h4"} component="h1" fontWeight="bold">
                {t('kitchenMenuSelection.title')}
              </Typography>
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ ml: isMobile ? 0 : 6 }}>
            {t('kitchenMenuSelection.subtitle')}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {menus.length === 0 && !error ? (
          <Paper elevation={3} sx={{ p: isMobile ? 3 : 4, textAlign: 'center' }}>
            <KitchenIcon sx={{ fontSize: isMobile ? 48 : 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('kitchenMenuSelection.emptyState.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {userRole === 'admin' 
                ? t('kitchenMenuSelection.emptyState.adminDescription')
                : t('kitchenMenuSelection.emptyState.staffDescription')
              }
            </Typography>
          </Paper>
        ) : isMobile ? (
          /* Mobil Kart Görünümü */
          <Box>
            {menus.map((menu) => (
              <MenuCard key={menu.id} onClick={() => handleSelectMenu(menu.id)}>
                <CardContent sx={{ pb: 1 }}>
                  {/* Menü Başlığı */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Box sx={{ 
                      bgcolor: 'primary.main', 
                      borderRadius: 1.5, 
                      p: 1, 
                      mr: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <KitchenIcon sx={{ color: 'white', fontSize: 24 }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                        {menu.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {menu.branchName || t('kitchenMenuSelection.table.branchUnassigned')}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
                
                {/* Butonlar */}
                <CardActions sx={{ px: 2, pb: 2, pt: 0, gap: 1 }}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<SettingsIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenSettingsModal(menu);
                    }}
                    size="medium"
                    sx={{ flex: 1 }}
                  >
                    {t('kitchenMenuSelection.actions.settings')}
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<KitchenIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectMenu(menu.id);
                    }}
                    size="large"
                    sx={{ 
                      flex: 2,
                      py: 1.5,
                      fontWeight: 'bold',
                      fontSize: '0.95rem'
                    }}
                  >
                    {t('kitchenMenuSelection.actions.openKitchenScreen')}
                  </Button>
                </CardActions>
              </MenuCard>
            ))}
          </Box>
        ) : (
          /* Desktop Tablo Görünümü */
          <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <TableContainer>
              <Table size="medium" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', width: '60px' }}>
                      <RestaurantMenuIcon />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', width: '35%' }}>
                      {t('kitchenMenuSelection.table.menuName')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', width: '25%' }}>
                      {t('kitchenMenuSelection.table.branch')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', textAlign: 'right', width: '40%' }}>
                      {t('kitchenMenuSelection.table.actions')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {menus.map((menu) => (
                    <StyledTableRow 
                      key={menu.id}
                      onClick={() => handleSelectMenu(menu.id)}
                    >
                      <TableCell>
                        <KitchenIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="h6" fontWeight="bold">
                          {menu.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" color="text.secondary">
                          {menu.branchName || t('kitchenMenuSelection.table.branchUnassigned')}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Button
                            variant="outlined"
                            color="secondary"
                            startIcon={<SettingsIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenSettingsModal(menu);
                            }}
                            size="small"
                          >
                            {t('kitchenMenuSelection.actions.settings')}
                          </Button>
                          <Button
                            variant="contained"
                            color="primary"
                            startIcon={<KitchenIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectMenu(menu.id);
                            }}
                            size="small"
                          >
                            {t('kitchenMenuSelection.actions.openKitchenScreen')}
                          </Button>
                        </Box>
                      </TableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </ContentArea>

      {/* QR Menü Ayarları Modal - Sadece Mutfak Ekranı Sekmelerini Göster */}
      {isSettingsModalOpen && selectedMenuForSettings && (
        <QRMenuSettingsModal
          open={isSettingsModalOpen}
          onClose={handleCloseSettingsModal}
          menuId={selectedMenuForSettings.id}
          companyId={companyId}
          menuName={selectedMenuForSettings.name}
          onImageUpload={handleUploadProductImage}
          isKitchenMode={true}
        />
      )}
    </PageContainer>
  );
};

export default KitchenMenuSelection;
